import { cn } from "@/lib/cn";
import { createContext, forwardRef, useContext, useId, type ReactNode } from "react";
import { useTextToneClass } from "./Typography";

/** Wiring a `Field` hands to the control inside it. */
export type FieldControl = {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
};

const FieldContext = createContext<FieldControl | null>(null);

/** The enclosing `Field`'s id, `aria-describedby`, invalid and required state, or `null` outside one. */
export function useFieldControl(): FieldControl | null {
  return useContext(FieldContext);
}

/** Cuts the enclosing `Field` wiring for portal/dialog content rendered inside a Field (its own controls keep their ids). */
export function FieldReset({ children }: { children: ReactNode }) {
  return <FieldContext.Provider value={null}>{children}</FieldContext.Provider>;
}

type ControlWiringInput = {
  id?: string;
  "aria-describedby"?: string;
  invalid?: boolean | null;
  required?: boolean;
};

/** Resolves a control's a11y attributes: explicit props win, the enclosing `Field` fills the gaps. */
export function useFieldWiring({ id, "aria-describedby": describedBy, invalid, required }: ControlWiringInput) {
  const field = useFieldControl();
  const resolvedInvalid = invalid ?? field?.invalid ?? false;
  const mergedDescribedBy = [describedBy, field?.describedBy].filter(Boolean).join(" ") || undefined;
  return {
    id: id ?? field?.id,
    "aria-describedby": mergedDescribedBy,
    "aria-invalid": resolvedInvalid || undefined,
    "aria-required": required || field?.required || undefined,
    invalid: resolvedInvalid,
  };
}

export type FieldLabelProps = {
  htmlFor: string;
  children: ReactNode;
  required?: boolean;
  hidden?: boolean;
  className?: string;
};

/** Field label recipe (semibold surface text, brand asterisk when required); `Field` renders it for you. */
export function FieldLabel({ htmlFor, children, required = false, hidden = false, className }: FieldLabelProps) {
  const toneClass = useTextToneClass("default");
  return (
    <label htmlFor={htmlFor} className={cn("text-base font-semibold", toneClass, hidden && "sr-only", className)}>
      {children}
      {required && (
        <span aria-hidden className="ml-0.5 text-primary">
          *
        </span>
      )}
    </label>
  );
}

/** Field error recipe, announced with `role="alert"`; `Field` renders it for you. */
export function FieldError({ id, children, className }: { id?: string; children: ReactNode; className?: string }) {
  return (
    <p id={id} role="alert" className={cn("text-sm font-semibold text-primary-red", className)}>
      {children}
    </p>
  );
}

export type FieldProps = {
  label: ReactNode;
  children: ReactNode;
  /** Supporting text under the control, linked through `aria-describedby`. */
  hint?: ReactNode;
  /** Error message; marks the control invalid and is announced (`role="alert"`). */
  error?: ReactNode;
  /** Shows the brand asterisk and sets `aria-required` on the control. */
  required?: boolean;
  /** Forces the invalid state without a message (the message lives elsewhere). */
  invalid?: boolean;
  /** Character counter shown at the end of the hint row. */
  counter?: { current: number; max: number };
  /** Keeps the label for assistive tech only. */
  hideLabel?: boolean;
  /** Overrides the generated control id (legacy ids used by tests or anchors). */
  id?: string;
  className?: string;
};

/** Label, hint, counter and error around one control, with ids wired through `useFieldControl()`. */
export const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(
  { label, children, hint, error, required = false, invalid, counter, hideLabel = false, id, className },
  ref,
) {
  const generated = useId();
  const controlId = id ?? `${generated}-control`;
  const hintId = `${generated}-hint`;
  const errorId = `${generated}-error`;
  const hasError = error !== undefined && error !== null && error !== false && error !== "";
  const describedBy = [hint ? hintId : null, hasError ? errorId : null].filter(Boolean).join(" ") || undefined;
  const hintToneClass = useTextToneClass("subtle");

  return (
    <FieldContext.Provider value={{ id: controlId, describedBy, invalid: invalid ?? hasError, required }}>
      <div ref={ref} className={cn("flex flex-col gap-2", className)}>
        <FieldLabel htmlFor={controlId} required={required} hidden={hideLabel}>
          {label}
        </FieldLabel>
        {children}
        {hint || counter ? (
          <div className={cn("flex items-start justify-between gap-2 text-xs", hintToneClass)}>
            {hint ? <p id={hintId}>{hint}</p> : <span />}
            {counter ? (
              <span className="shrink-0 tabular-nums">
                {counter.current}/{counter.max}
              </span>
            ) : null}
          </div>
        ) : null}
        {hasError ? <FieldError id={errorId}>{error}</FieldError> : null}
      </div>
    </FieldContext.Provider>
  );
});
